const SVG_NS = "http://www.w3.org/2000/svg";

function toFiniteNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function clampNormalized(value: unknown): number | null {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) return null;
  return Math.min(1, Math.max(0, normalized));
}

export interface PlanCoordsOptions {
  viewBoxX?: number;
  viewBoxY?: number;
  viewBoxWidth?: number;
  viewBoxHeight?: number;
  imageRect?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    naturalWidth?: number;
    naturalHeight?: number;
    preserveAspectRatio?: string;
  };
}

export function planCoordsFromClient(
  svg: SVGSVGElement | null,
  clientX: number,
  clientY: number,
  options: PlanCoordsOptions = {},
): { x: number; y: number } | null {
  const imageRect = computeVisibleImageRect(options?.imageRect);
  const ctm = svg?.getScreenCTM?.() || null;
  if (!ctm) return null;

  const point =
    typeof DOMPoint === "function" ? new DOMPoint(clientX, clientY) : svg!.createSVGPoint();
  if (typeof DOMPoint !== "function") {
    (point as DOMPoint).x = clientX;
    (point as DOMPoint).y = clientY;
  }
  const svgPoint = point.matrixTransform(ctm.inverse());

  const bounds = imageRect || {
    x: toFiniteNumber(options?.viewBoxX, 0),
    y: toFiniteNumber(options?.viewBoxY, 0),
    width: toFiniteNumber(options?.viewBoxWidth, 1),
    height: toFiniteNumber(options?.viewBoxHeight, 1),
  };
  if (bounds.width <= 0 || bounds.height <= 0) return null;

  const x = clampNormalized((svgPoint.x - bounds.x) / bounds.width);
  const y = clampNormalized((svgPoint.y - bounds.y) / bounds.height);
  if (x === null || y === null) return null;
  return { x, y };
}

function resolvePreserveAspectRatio(value: string | undefined) {
  const raw = String(value || "").trim();
  if (!raw || raw === "defer") return { align: "xMidYMid", meetOrSlice: "meet" as const };
  const parts = raw.split(/\s+/).filter(Boolean);
  const filtered = parts.filter((part) => part.toLowerCase() !== "defer");
  const align = filtered[0] || "xMidYMid";
  const meetOrSlice = filtered[1] === "slice" ? ("slice" as const) : ("meet" as const);
  return { align, meetOrSlice };
}

function computeVisibleImageRect(imageRect: PlanCoordsOptions["imageRect"] = {}) {
  const boxX = toFiniteNumber(imageRect?.x, 0);
  const boxY = toFiniteNumber(imageRect?.y, 0);
  const boxWidth = toFiniteNumber(imageRect?.width, 0);
  const boxHeight = toFiniteNumber(imageRect?.height, 0);
  if (boxWidth <= 0 || boxHeight <= 0) return null;

  const naturalWidth = toFiniteNumber(imageRect?.naturalWidth, boxWidth);
  const naturalHeight = toFiniteNumber(imageRect?.naturalHeight, boxHeight);
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return { x: boxX, y: boxY, width: boxWidth, height: boxHeight };
  }

  const { align, meetOrSlice } = resolvePreserveAspectRatio(imageRect?.preserveAspectRatio);
  if (align === "none") return { x: boxX, y: boxY, width: boxWidth, height: boxHeight };

  const scaleX = boxWidth / naturalWidth;
  const scaleY = boxHeight / naturalHeight;
  const scale = meetOrSlice === "slice" ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;

  let offsetX = 0;
  let offsetY = 0;
  if (align.includes("xMid")) offsetX = (boxWidth - width) / 2;
  else if (align.includes("xMax")) offsetX = boxWidth - width;
  if (align.includes("YMid")) offsetY = (boxHeight - height) / 2;
  else if (align.includes("YMax")) offsetY = boxHeight - height;

  return { x: boxX + offsetX, y: boxY + offsetY, width, height };
}

function createSvgNode(tagName: string) {
  return document.createElementNS(SVG_NS, tagName);
}

function applyDataAttributes(node: Element, dataset: Record<string, unknown> = {}) {
  Object.entries(dataset || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    (node as HTMLElement).dataset[key] = String(value);
  });
}

function resolveScreenValue(value: unknown, fallback: number, screenScale = 1) {
  const numeric = Number(value);
  const baseValue = Number.isFinite(numeric) ? numeric : fallback;
  const scale = Number.isFinite(Number(screenScale)) && Number(screenScale) > 0 ? Number(screenScale) : 1;
  return baseValue / scale;
}

function planMarkerPngToDataUrl(raw: unknown) {
  const s = String(raw || "").trim();
  if (!s) return "";
  return s.startsWith("data:") ? s : `data:image/png;base64,${s}`;
}

function attachPlanMarkerRasterImage(icon: SVGImageElement, href: string) {
  icon.setAttribute("href", href);
  try {
    icon.setAttributeNS("http://www.w3.org/1999/xlink", "href", href);
  } catch {
    /* ignore */
  }
}

export interface PlanMarkerInput {
  point?: { x: number; y: number } | null;
  label?: { x: number; y: number } | null;
  labelText?: string;
  labelSvgMarkup?: string;
  labelPngBase64?: string;
  labelPngHref?: string;
  labelSymbolSize?: number;
  labelSymbolGap?: number;
  pointClass?: string;
  lineClass?: string;
  labelGroupClass?: string;
  labelTextClass?: string;
  labelBadgeClass?: string;
  labelSymbolClass?: string;
  screenScale?: number;
  pointRadius?: number;
  pointTabIndex?: number;
  pointRole?: string;
  pointAriaLabel?: string;
  pointDataset?: Record<string, unknown> | null;
  labelTabIndex?: number;
  labelRole?: string;
  labelAriaLabel?: string;
  labelDataset?: Record<string, unknown> | null;
  labelPaddingX?: number;
  labelPaddingY?: number;
  labelRadiusX?: number;
  labelRadiusY?: number;
  labelFontSize?: number;
  labelStrokeWidth?: number;
  labelBadgeStrokeWidth?: number;
  title?: string;
  dataset?: Record<string, unknown>;
}

function renderMarker(layer: SVGGElement, marker: PlanMarkerInput) {
  const point = marker?.point || null;
  const label = marker?.label || null;
  const hasPoint = Number.isFinite(point?.x) && Number.isFinite(point?.y);
  const hasLabel = Number.isFinite(label?.x) && Number.isFinite(label?.y);
  if (!hasPoint && !hasLabel) return;

  const effectiveLabel = hasLabel ? label : point;
  const screenScale =
    Number.isFinite(Number(marker?.screenScale)) && Number(marker.screenScale) > 0
      ? Number(marker.screenScale)
      : 1;

  let line: SVGLineElement | null = null;
  if (hasPoint && hasLabel && point!.x !== label!.x && point!.y !== label!.y) {
    line = createSvgNode("line") as SVGLineElement;
    line.setAttribute("class", marker?.lineClass || "plan-marker-line");
    line.setAttribute("x1", String(point!.x));
    line.setAttribute("y1", String(point!.y));
    line.setAttribute("x2", String(label!.x));
    line.setAttribute("y2", String(label!.y));
    layer.appendChild(line);
  }

  if (hasPoint) {
    const circle = createSvgNode("circle") as SVGCircleElement;
    circle.setAttribute("class", marker?.pointClass || "plan-marker-point");
    circle.setAttribute("cx", String(point!.x));
    circle.setAttribute("cy", String(point!.y));
    circle.setAttribute("r", String(resolveScreenValue(marker?.pointRadius, 4.5, screenScale)));
    if (marker?.pointTabIndex !== undefined) circle.tabIndex = marker.pointTabIndex;
    if (marker?.pointRole) circle.setAttribute("role", marker.pointRole);
    if (marker?.pointAriaLabel) circle.setAttribute("aria-label", marker.pointAriaLabel);
    if (marker?.title) {
      const title = createSvgNode("title");
      title.textContent = String(marker.title);
      circle.appendChild(title);
    }
    applyDataAttributes(circle, marker?.pointDataset || marker?.dataset || {});
    layer.appendChild(circle);
  }

  const labelSvgRaw = String(marker?.labelSvgMarkup || "").trim();
  const labelPngHrefDirect = String(marker?.labelPngHref || "").trim();
  const labelPngHref = labelPngHrefDirect || planMarkerPngToDataUrl(marker?.labelPngBase64);
  const trimmedMarkerLabelText = String(marker?.labelText ?? "").trim();
  const hasPlanSymbol = Boolean(labelSvgRaw || labelPngHref);
  const showStandardTextBadge = Boolean(trimmedMarkerLabelText) && !hasPlanSymbol;
  const hasLabelVisual = Boolean(showStandardTextBadge || hasPlanSymbol);
  if (effectiveLabel && hasLabelVisual) {
    const labelGroup = createSvgNode("g") as SVGGElement;
    labelGroup.setAttribute("class", marker?.labelGroupClass || "plan-marker-label-group");
    if (marker?.labelTabIndex !== undefined) labelGroup.tabIndex = marker.labelTabIndex;
    if (marker?.labelRole) labelGroup.setAttribute("role", marker.labelRole);
    if (marker?.labelAriaLabel) labelGroup.setAttribute("aria-label", marker.labelAriaLabel);
    applyDataAttributes(labelGroup, marker?.labelDataset || marker?.dataset || {});
    layer.appendChild(labelGroup);

    const iconSize = resolveScreenValue(marker?.labelSymbolSize, 14, screenScale);
    const iconGap = resolveScreenValue(marker?.labelSymbolGap, 5, screenScale);
    let cursorX = effectiveLabel.x;

    let symbolImageNode: SVGImageElement | null = null;
    if (labelSvgRaw) {
      const icon = createSvgNode("image") as SVGImageElement;
      icon.setAttribute("class", marker?.labelSymbolClass || "plan-marker-label-symbol");
      attachPlanMarkerRasterImage(
        icon,
        `data:image/svg+xml;charset=utf-8,${encodeURIComponent(labelSvgRaw)}`,
      );
      icon.setAttribute("x", String(cursorX));
      icon.setAttribute("y", String(effectiveLabel.y - iconSize / 2));
      icon.setAttribute("width", String(iconSize));
      icon.setAttribute("height", String(iconSize));
      icon.setAttribute("preserveAspectRatio", "xMidYMid meet");
      labelGroup.appendChild(icon);
      symbolImageNode = icon;
      cursorX += iconSize + iconGap;
    } else if (labelPngHref) {
      const icon = createSvgNode("image") as SVGImageElement;
      icon.setAttribute("class", marker?.labelSymbolClass || "plan-marker-label-symbol");
      attachPlanMarkerRasterImage(icon, labelPngHref);
      icon.setAttribute("x", String(cursorX));
      icon.setAttribute("y", String(effectiveLabel.y - iconSize / 2));
      icon.setAttribute("width", String(iconSize));
      icon.setAttribute("height", String(iconSize));
      icon.setAttribute("preserveAspectRatio", "xMidYMid meet");
      labelGroup.appendChild(icon);
      symbolImageNode = icon;
      cursorX += iconSize + iconGap;
    }

    if (hasPlanSymbol && trimmedMarkerLabelText && symbolImageNode) {
      const titleNode = createSvgNode("title");
      titleNode.textContent = trimmedMarkerLabelText;
      symbolImageNode.appendChild(titleNode);
    }

    let labelTextNode: SVGTextElement | null = null;
    if (showStandardTextBadge) {
      labelTextNode = createSvgNode("text") as SVGTextElement;
      labelTextNode.setAttribute("class", marker?.labelTextClass || "plan-marker-label");
      labelTextNode.setAttribute("x", String(cursorX));
      labelTextNode.setAttribute("y", String(effectiveLabel.y));
      labelTextNode.setAttribute("dominant-baseline", "middle");
      labelTextNode.setAttribute("font-size", String(resolveScreenValue(marker?.labelFontSize, 28, screenScale)));
      labelTextNode.setAttribute(
        "stroke-width",
        String(resolveScreenValue(marker?.labelStrokeWidth, 10, screenScale)),
      );
      labelTextNode.textContent = trimmedMarkerLabelText;
      labelGroup.appendChild(labelTextNode);
    }

    if (showStandardTextBadge && labelTextNode) {
      const bbox = labelGroup.getBBox();
      const paddingX = resolveScreenValue(marker?.labelPaddingX, 12, screenScale);
      const paddingY = resolveScreenValue(marker?.labelPaddingY, 8, screenScale);
      const rect = createSvgNode("rect") as SVGRectElement;
      rect.setAttribute("class", marker?.labelBadgeClass || "plan-marker-label-badge");
      rect.setAttribute("x", String(bbox.x - paddingX));
      rect.setAttribute("y", String(bbox.y - paddingY));
      rect.setAttribute("width", String(bbox.width + paddingX * 2));
      rect.setAttribute("height", String(bbox.height + paddingY * 2));
      rect.setAttribute("rx", String(resolveScreenValue(marker?.labelRadiusX, 12, screenScale)));
      rect.setAttribute("ry", String(resolveScreenValue(marker?.labelRadiusY, 12, screenScale)));
      rect.setAttribute(
        "stroke-width",
        String(resolveScreenValue(marker?.labelBadgeStrokeWidth, 3, screenScale)),
      );
      labelGroup.insertBefore(rect, labelTextNode);
    }

    if (line && hasPoint) {
      const bb = labelGroup.getBBox();
      const rectCenterX = bb.x + bb.width / 2;
      const rectCenterY = bb.y + bb.height / 2;
      const deltaX = rectCenterX - point!.x;
      const deltaY = rectCenterY - point!.y;
      const halfWidth = bb.width / 2;
      const halfHeight = bb.height / 2;
      const scaleX = Math.abs(deltaX) > 0 ? halfWidth / Math.abs(deltaX) : Number.POSITIVE_INFINITY;
      const scaleY = Math.abs(deltaY) > 0 ? halfHeight / Math.abs(deltaY) : Number.POSITIVE_INFINITY;
      const edgeScale = Math.min(scaleX, scaleY);
      const lineEndX = Number.isFinite(edgeScale) ? rectCenterX - deltaX * edgeScale : rectCenterX;
      const lineEndY = Number.isFinite(edgeScale) ? rectCenterY - deltaY * edgeScale : rectCenterY;
      line.setAttribute("x2", String(lineEndX));
      line.setAttribute("y2", String(lineEndY));
    }
  }
}

export interface RenderPlanSvgOptions {
  svg: SVGSVGElement | null;
  image: SVGImageElement | null;
  layer: SVGGElement | null;
  meta?: HTMLElement | null;
  hint?: HTMLElement | null;
  plan?: unknown;
  emptyViewBox?: string;
  imageHref?: string;
  imageX?: number;
  imageY?: number;
  imageWidth?: number;
  imageHeight?: number;
  viewBox?: string;
  markers?: PlanMarkerInput[];
  metaText?: string;
  hintText?: string;
  emptyHintText?: string;
}

export function renderPlanSvg({
  svg,
  image,
  layer,
  meta,
  hint,
  plan = null,
  emptyViewBox = "0 0 1 1",
  imageHref = "",
  imageX = 0,
  imageY = 0,
  imageWidth = 1,
  imageHeight = 1,
  viewBox,
  markers = [],
  metaText = "",
  hintText = "",
  emptyHintText = "",
}: RenderPlanSvgOptions) {
  if (!(svg instanceof SVGSVGElement) || !(image instanceof SVGImageElement) || !(layer instanceof SVGGElement))
    return;

  if (!plan) {
    svg.setAttribute("viewBox", emptyViewBox);
    image.setAttribute("href", "");
    image.setAttribute("x", "0");
    image.setAttribute("y", "0");
    image.setAttribute("width", emptyViewBox.split(" ")[2] || "1");
    image.setAttribute("height", emptyViewBox.split(" ")[3] || "1");
    layer.innerHTML = "";
    if (meta) meta.textContent = "";
    if (hint) hint.textContent = emptyHintText;
    return;
  }

  svg.setAttribute("viewBox", viewBox || emptyViewBox);
  image.setAttribute("href", String(imageHref || ""));
  image.setAttribute("x", String(imageX));
  image.setAttribute("y", String(imageY));
  image.setAttribute("width", String(imageWidth));
  image.setAttribute("height", String(imageHeight));

  layer.innerHTML = "";
  (Array.isArray(markers) ? markers : []).forEach((marker) => renderMarker(layer, marker));

  if (meta) meta.textContent = String(metaText || "");
  if (hint) hint.textContent = String(hintText || "");
}
